import {formatMoney} from '@shopify/theme-currency';

import hideElement from '../util/hide-element';
import showElement from '../util/show-element';
import {ProductForm} from '../vendor/theme-scripts/theme-product-form';

import {initQtySection} from './quantity-selector';
import {RecordRecentlyViewed} from './recent-products';

const selectors = {
  productForm: '[data-product-form]',
  productSlideshow: '[data-product-slideshow]',
  addToCart: '[data-add-to-cart]',
  addToCartText: '[data-add-to-cart-text]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  buttonsWrapper: '[data-buttons-wrapper]',
  originalSelectorId: '[data-product-select]',
  priceWrapper: '[data-price-wrapper]',
  priceButton: '[data-button-price]',
  productJson: '[data-product-json]',
  productPrice: '[data-product-price]',
  unitPrice: '[data-product-unit-price]',
  unitBase: '[data-product-base]',
  unitWrapper: '[data-product-unit]',
  dataEnableHistoryState: 'data-enable-history-state',
  optionPosition: 'data-option-position',
  optionValue: '[data-option-value]',
  subPrices: '[data-subscription-watch-price]',
  subSelectors: '[data-subscription-selectors]',
  priceOffWrap: '[data-price-off]',
  priceOffType: '[data-price-off-type]',
  priceOffAmount: '[data-price-off-amount]',
  subsToggle: '[data-toggles-group]',
  subsChild: 'data-group-toggle',
  subDescription: '[data-plan-description]',
  remainingCount: '[data-remaining-count]',
  remainingMax: '[data-remaining-max]',
  remainingMaxAttr: 'data-remaining-max',
  remainingWrapper: '[data-remaining-wrapper]',
  remainingJSON: '[data-product-remaining-json]',
  preOrderTag: '_preorder',
  idInput: '[name="id"]',
};

const classes = {
  hide: 'hide',
  variantSoldOut: 'variant--soldout',
  variantUnavailable: 'variant--unavailable',
  productPriceSale: 'product__price--sale',
  showRemaining: 'variant__countdown--show',
};

class ProductAddForm {
  constructor(section) {
    this.section = section;
    this.container = section.container;

    this.productForm = this.container.querySelector(selectors.productForm);
    this.slideshow = this.container.querySelector(selectors.productSlideshow);
    this.enableHistoryState = this.container.getAttribute(selectors.dataEnableHistoryState) === 'true';
    this.hasUnitPricing = this.container.querySelector(selectors.unitWrapper);
    this.subSelectors = this.container.querySelector(selectors.subSelectors);
    this.subPrices = this.container.querySelector(selectors.subPrices);

    this.priceOffWrap = this.container.querySelector(selectors.priceOffWrap);
    this.priceOffAmount = this.container.querySelector(selectors.priceOffAmount);
    this.planDecription = this.container.querySelector(selectors.subDescription);
    this.priceOffType = this.container.querySelector(selectors.priceOffType);

    this.remainingWrapper = this.container.querySelector(selectors.remainingWrapper);
    if (this.remainingWrapper) {
      const remainingMaxWrap = this.container.querySelector(selectors.remainingMax);
      this.remainingMaxInt = parseInt(remainingMaxWrap.getAttribute(selectors.remainingMaxAttr), 10);
      this.remainingCount = this.container.querySelector(selectors.remainingCount);
      this.remainingJSONWrapper = this.container.querySelector(selectors.remainingJSON);
      this.remainingJSON = null;
      if (this.remainingJSONWrapper && this.remainingJSONWrapper.innerHTML !== '') {
        this.remainingJSON = JSON.parse(this.remainingJSONWrapper.innerHTML);
      } else {
        console.warn('Missing product quantity JSON');
      }
    }

    initQtySection(this.container);

    this.init();
  }

  init() {
    let productJSON = null;
    const productElemJSON = this.container.querySelector(selectors.productJson);

    if (productElemJSON) {
      productJSON = productElemJSON.innerHTML;
    }
    if (productJSON && this.productForm) {
      this.productJSON = JSON.parse(productJSON);
      this.linkForm();
    } else {
      console.error('Missing product form or product JSON');
    }

    // Add cookie for recent products
    new RecordRecentlyViewed(this.productJSON.handle);
  }

  destroy() {
    this.productForm.destroy();
  }

  linkForm() {
    this.productForm = new ProductForm(this.productForm, this.productJSON, {
      onOptionChange: this.onOptionChange.bind(this),
      onPlanChange: this.onPlanChange.bind(this),
      onQuantityChange: this.onQuantityChange.bind(this),
    });
    this.pushState(this.productForm.getFormState());
    this.subsToggleListeners();
  }

  onOptionChange(evt) {
    this.pushState(evt.dataset);
  }

  onPlanChange(evt) {
    if (this.subPrices) {
      this.pushState(evt.dataset);
    }
  }

  onQuantityChange(evt) {
    const formState = evt.dataset;
    this.productState = this.setProductState(formState);
    this.updateButtonPrices(formState);
  }

  pushState(formState) {
    this.productState = this.setProductState(formState);
    this.updateProductImage(formState);
    this.updateAddToCartState(formState);
    this.updateProductPrices(formState);
    this.updateSaleText(formState);
    this.updateSubscriptionText(formState);
    this.updateLegend(formState);
    this.updateRemaining(formState);
    this.fireHookEvent(formState);
    if (this.enableHistoryState) {
      this.updateHistoryState(formState);
    }
  }

  updateAddToCartState(formState) {
    const variant = formState.variant;
    let addText = theme.strings.addToCart;
    const priceWrapper = this.container.querySelectorAll(selectors.priceWrapper);
    const buttonsWrapper = this.container.querySelector(selectors.buttonsWrapper);
    const addToCart = buttonsWrapper.querySelectorAll(selectors.addToCart);
    const addToCartText = buttonsWrapper.querySelectorAll(selectors.addToCartText);

    if (this.productJSON.tags.includes(selectors.preOrderTag)) {
      addText = theme.strings.preOrder;
    }

    if (priceWrapper.length && variant) {
      priceWrapper.forEach((element) => {
        element.classList.remove(classes.hide);
      });
    }

    if (addToCart.length) {
      addToCart.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.disabled = false;
          } else {
            element.disabled = true;
          }
        } else {
          element.disabled = true;
        }
      });
    }

    if (addToCartText.length) {
      addToCartText.forEach((element) => {
        if (variant) {
          if (variant.available) {
            element.innerHTML = addText;
          } else {
            element.innerHTML = theme.strings.soldOut;
          }
        } else {
          element.innerHTML = theme.strings.unavailable;
        }
      });
    }

    if (buttonsWrapper) {
      if (variant) {
        if (variant.available) {
          buttonsWrapper.classList.remove(classes.variantSoldOut, classes.variantUnavailable);
        } else {
          buttonsWrapper.classList.add(classes.variantSoldOut);
          buttonsWrapper.classList.remove(classes.variantUnavailable);
        }
        const formSelect = buttonsWrapper.querySelector(selectors.originalSelectorId);
        if (formSelect) {
          formSelect.value = variant.id;
        }
      } else {
        buttonsWrapper.classList.add(classes.variantUnavailable);
        buttonsWrapper.classList.remove(classes.variantSoldOut);
      }
    }
  }

  updateLegend(formState) {
    const variant = formState.variant;
    if (variant) {
      const vals = this.container.querySelectorAll(selectors.optionValue);
      vals.forEach((val) => {
        const wrapper = val.closest(`[${selectors.optionPosition}]`);
        if (wrapper) {
          const position = wrapper.getAttribute(selectors.optionPosition);
          const index = parseInt(position, 10) - 1;
          const newValue = variant.options[index];
          val.innerHTML = newValue;
        }
      });
    }
  }

  updateHistoryState(formState) {
    const variant = formState.variant;
    const plan = formState.plan;
    const location = window.location.href;
    if (variant && location.includes('/product')) {
      const url = new window.URL(location);
      const params = url.searchParams;
      params.set('variant', variant.id);
      if (plan && plan.detail && plan.detail.id && this.productState.hasPlan) {
        params.set('selling_plan', plan.detail.id);
      } else {
        params.delete('selling_plan');
      }
      url.search = params.toString();
      const urlString = url.toString();
      window.history.replaceState({path: urlString}, '', urlString);
    }
  }

  updateRemaining(formState) {
    const variant = formState.variant;
    if (variant && this.remainingWrapper && this.remainingJSON && this.remainingCount) {
      const newQuantity = this.remainingJSON[variant.id];
      if (newQuantity && newQuantity <= this.remainingMaxInt && newQuantity > 0) {
        this.remainingWrapper.classList.add(classes.showRemaining);
        this.remainingCount.innerHTML = newQuantity;
      } else {
        this.remainingWrapper.classList.remove(classes.showRemaining);
      }
    } else if (this.remainingWrapper) {
      this.remainingWrapper.classList.remove(classes.showRemaining);
    }
  }

  getBaseUnit(variant) {
    return variant.unit_price_measurement.reference_value === 1
      ? variant.unit_price_measurement.reference_unit
      : variant.unit_price_measurement.reference_value + variant.unit_price_measurement.reference_unit;
  }

  subsToggleListeners() {
    const toggles = this.container.querySelectorAll(selectors.subsToggle);

    toggles.forEach((toggle) => {
      toggle.addEventListener(
        'change',
        function (e) {
          const val = e.target.value.toString();
          const selected = this.container.querySelector(`[${selectors.subsChild}="${val}"]`);
          const groups = this.container.querySelectorAll(`[${selectors.subsChild}]`);
          if (selected) {
            selected.classList.remove(classes.hide);
            const first = selected.querySelector(`[name="selling_plan"]`);
            first.checked = true;
            first.dispatchEvent(new Event('change'));
          }
          groups.forEach((group) => {
            if (group !== selected) {
              group.classList.add(classes.hide);
              const plans = group.querySelectorAll(`[name="selling_plan"]`);
              plans.forEach((plan) => {
                plan.checked = false;
                plan.dispatchEvent(new Event('change'));
              });
            }
          });
        }.bind(this)
      );
    });
  }

  updateSaleText(formState) {
    if (this.productState.planSale) {
      this.updateSaleTextSubscription(formState);
    } else if (this.productState.onSale) {
      this.updateSaleTextStandard(formState);
    } else {
      // this.priceOffWrap.classList.add(classes.hide);
    }
  }

  updateSaleTextStandard(formState) {
    if (!this.priceOffType) return;
    this.priceOffType.innerHTML = window.theme.strings.sale || 'sale';
    const variant = formState.variant;
    if (window.theme.settings.badge_sale_type && window.theme.settings.badge_sale_type === 'percentage') {
      const discountFloat = (variant.compare_at_price - variant.price) / variant.compare_at_price;
      const discountInt = Math.floor(discountFloat * 100);
      this.priceOffAmount.innerHTML = `${discountInt}%`;
    } else {
      const discount = variant.compare_at_price - variant.price;
      this.priceOffAmount.innerHTML = formatMoney(discount, theme.moneyFormat);
    }
    // this.priceOffWrap.classList.remove(classes.hide);
  }

  updateSaleTextSubscription(formState) {
    this.priceOffType.innerHTML = window.theme.strings.subscription || 'subscripton';
    const adjustment = formState.plan.detail.price_adjustments[0];
    const discount = adjustment.value;
    if (adjustment && adjustment.value_type === 'percentage') {
      this.priceOffAmount.innerHTML = `${discount}%`;
    } else {
      this.priceOffAmount.innerHTML = formatMoney(discount, theme.moneyFormat);
    }
    // this.priceOffWrap.classList.remove(classes.hide);
  }

  updateSubscriptionText(formState) {
    if (formState.plan && this.planDecription) {
      this.planDecription.innerHTML = formState.plan.detail.description;
      this.planDecription.classList.remove(classes.hide);
    } else if (this.planDecription) {
      this.planDecription.classList.add(classes.hide);
    }
  }

  getPrices(formState) {
    const variant = formState.variant;
    const plan = formState.plan;
    let comparePrice = '';
    let price = '';

    if (this.productState.available) {
      comparePrice = variant.compare_at_price;
      price = variant.price;
    }

    if (this.productState.hasPlan) {
      price = plan.allocation.price;
    }

    if (this.productState.planSale) {
      comparePrice = plan.allocation.compare_at_price;
      price = plan.allocation.price;
    }
    return {
      price: price,
      comparePrice: comparePrice,
    };
  }

  updateButtonPrices(formState) {
    const priceButtons = this.container.querySelectorAll(selectors.priceButton);
    const {price} = this.getPrices(formState);

    if (priceButtons.length) {
      priceButtons.forEach((btn) => {
        const btnPrice = formState.quantity * price;
        btn.innerHTML = formatMoney(btnPrice, theme.moneyFormat);
      });
    }
  }

  updateProductPrices(formState) {
    const variant = formState.variant;
    const priceWrappers = this.container.querySelectorAll(selectors.priceWrapper);
    const priceButtons = this.container.querySelectorAll(selectors.priceButton);

    const {price, comparePrice} = this.getPrices(formState);

    priceWrappers.forEach((wrap) => {
      const comparePriceEl = wrap.querySelector(selectors.comparePrice);
      const productPriceEl = wrap.querySelector(selectors.productPrice);
      const comparePriceText = wrap.querySelector(selectors.comparePriceText);

      if (comparePriceEl) {
        if (this.productState.onSale || this.productState.planSale) {
          comparePriceEl.classList.remove(classes.hide);
          // comparePriceText.classList.remove(classes.hide);
          productPriceEl.classList.add(classes.productPriceSale);
        } else {
          comparePriceEl.classList.add(classes.hide);
          // comparePriceText.classList.add(classes.hide);
          productPriceEl.classList.remove(classes.productPriceSale);
        }
        comparePriceEl.innerHTML = formatMoney(comparePrice, theme.moneyFormat);
      }
      if (productPriceEl) {
        if (variant) {
          productPriceEl.innerHTML = formatMoney(price, theme.moneyFormat);
        } else {
          productPriceEl.innerHTML = '&nbsp;';
        }
      }
    });

    if (priceButtons.length) {
      priceButtons.forEach((btn) => {
        const btnPrice = formState.quantity * price;
        btn.innerHTML = formatMoney(btnPrice, theme.moneyFormat);
      });
    }

    if (this.hasUnitPricing) {
      this.updateProductUnits(formState);
    }
  }

  updateProductUnits(formState) {
    const variant = formState.variant;
    const plan = formState.plan;
    let unitPrice = null;

    if (variant && variant.unit_price) {
      unitPrice = variant.unit_price;
    }
    if (plan && plan.allocation && plan.allocation.unit_price) {
      unitPrice = plan.allocation.unit_price;
    }

    if (unitPrice) {
      const base = this.getBaseUnit(variant);
      const formattedPrice = formatMoney(unitPrice, theme.moneyFormat);
      this.container.querySelector(selectors.unitPrice).innerHTML = formattedPrice;
      this.container.querySelector(selectors.unitBase).innerHTML = base;
      showElement(this.container.querySelector(selectors.unitWrapper));
    } else {
      hideElement(this.container.querySelector(selectors.unitWrapper));
    }
  }

  fireHookEvent(formState) {
    const variant = formState.variant;
    this.container.dispatchEvent(
      new CustomEvent('theme:variant:change', {
        detail: {
          variant: variant,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Tracks aspects of the product state that are relevant to UI updates
   * @param {object} evt - variant change event
   * @return {object} productState - represents state of variant + plans
   *  productState.available - current variant and selling plan options result in valid offer
   *  productState.soldOut - variant is sold out
   *  productState.onSale - variant is on sale
   *  productState.showUnitPrice - variant has unit price
   *  productState.requiresPlan - all the product variants requires a selling plan
   *  productState.hasPlan - there is a valid selling plan
   *  productState.planSale - plan has a discount to show next to price
   *  productState.planPerDelivery - plan price does not equal per_delivery_price - a prepaid subscribtion
   */
  setProductState(dataset) {
    const variant = dataset.variant;
    const plan = dataset.plan;

    const productState = {
      available: true,
      soldOut: false,
      onSale: false,
      showUnitPrice: false,
      requiresPlan: false,
      hasPlan: false,
      planPerDelivery: false,
      planSale: false,
    };

    if (!variant || (variant.requires_selling_plan && !plan)) {
      productState.available = false;
    } else {
      if (!variant.available) {
        productState.soldOut = true;
      }

      if (variant.compare_at_price > variant.price) {
        productState.onSale = true;
      }

      if (variant.unit_price) {
        productState.showUnitPrice = true;
      }

      if (this.productJSON && this.productJSON.requires_selling_plan) {
        productState.requiresPlan = true;
      }

      if (plan && this.subPrices) {
        productState.hasPlan = true;
        if (plan.allocation.per_delivery_price !== plan.allocation.price) {
          productState.planPerDelivery = true;
        }
        if (variant.price > plan.allocation.price) {
          productState.planSale = true;
        }
      }
    }
    return productState;
  }

  updateProductImage(formState) {
    const variant = formState.variant;
    if (this.slideshow && variant && variant.featured_media && variant.featured_media.id) {
      // Update variant image, if one is set
      this.slideshow.dispatchEvent(
        new CustomEvent('theme:image:change', {
          detail: {
            id: variant.featured_media.id,
          },
        })
      );
    }
  }
}

const productFormSection = {
  onLoad() {
    this.section = new ProductAddForm(this);
  },
};

export {productFormSection};
